/* --- Complete Fixed Scroll Animation Logic --- */

// The observer that will watch for new elements being added to the page.
let pageObserver;

// The observer that watches individual elements for visibility.
let elementObserver;

// Keep track of elements that have already been animated
const animatedElements = new WeakSet();

// Track if we're currently in a filter update
let isFilterUpdate = false;

const setupElementObserver = () => {
  if (!('IntersectionObserver' in window)) {
    // Fallback: make all elements visible immediately
    document.querySelectorAll('.scroll-trigger').forEach(el => {
      el.classList.add('is-visible');
    });
    return;
  }

  elementObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      // Only animate elements that aren't already animated AND are in the viewport
      if (entry.isIntersecting && !animatedElements.has(entry.target)) {
        // Use requestAnimationFrame to ensure smooth animation
        requestAnimationFrame(() => {
          entry.target.classList.add('is-visible');
          animatedElements.add(entry.target);
        });
        
        // Once animated, stop observing this element to improve performance
        elementObserver.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.05, // Reduced threshold for earlier triggering
    rootMargin: '100px 0px 0px 0px' // Trigger 100px before element enters viewport
  });
};

const observeElements = (elements) => {
  if (!elementObserver) {
    // Fallback: make all elements visible immediately
    elements.forEach(el => el.classList.add('is-visible'));
    return;
  }

  if (elements.length === 0) {
    return;
  }

  elements.forEach(element => {
    // Skip elements that have already been animated
    if (animatedElements.has(element)) {
      return;
    }

    // Check if element is already in viewport before observing
    const rect = element.getBoundingClientRect();
    const isAlreadyVisible = (
      rect.top < (window.innerHeight + 100) && 
      rect.bottom > -100
    );

    if (isAlreadyVisible || isFilterUpdate) {
      // If already visible OR we're in a filter update, add class immediately
      element.classList.add('is-visible');
      animatedElements.add(element);
    } else {
      // Otherwise, observe for scroll animation
      elementObserver.observe(element);
    }
  });
};

const setupPageObserver = () => {
  if (!('MutationObserver' in window)) {
    return;
  }

  pageObserver = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        const newElements = [];
        mutation.addedNodes.forEach(node => {
          // Skip text nodes
          if (node.nodeType !== 1) return;
          
          // Check if the added node itself is a scroll-trigger
          if (node.matches && node.matches('.scroll-trigger')) {
            newElements.push(node);
          }
          // Also check for scroll-triggers within the added node
          if (node.querySelectorAll) {
            node.querySelectorAll('.scroll-trigger').forEach(el => newElements.push(el));
          }
        });

        if (newElements.length > 0) {
          // Use double requestAnimationFrame to ensure DOM is fully painted
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              observeElements(newElements);
            });
          });
        }
      }
    }
  });

  // Start observing the whole document for changes.
  pageObserver.observe(document.body, { childList: true, subtree: true });
};

// Handle filter updates (when products are dynamically loaded)
const handleFilterUpdate = () => {
  isFilterUpdate = true;
  
  // Small delay to ensure DOM is updated
  setTimeout(() => {
    // Re-scan for any new scroll-trigger elements
    const newElements = document.querySelectorAll('.scroll-trigger:not(.is-visible)');
    
    if (newElements.length > 0) {
      // Make all filtered products visible immediately
      newElements.forEach(el => {
        el.classList.add('is-visible');
        animatedElements.add(el);
      });
    }
    
    // Reset filter update flag after a delay
    setTimeout(() => {
      isFilterUpdate = false;
    }, 500);
  }, 100);
};

// Listen for various update events
document.addEventListener('facets:updated', handleFilterUpdate);
document.addEventListener('shopify:section:load', handleFilterUpdate);
document.addEventListener('collection:updated', handleFilterUpdate);

// Watch for changes in the main collection grid specifically
const watchCollectionGrid = () => {
  const collectionGrid = document.querySelector('.main-collection-grid, .product-grid, results-list');
  
  if (collectionGrid && 'MutationObserver' in window) {
    const gridObserver = new MutationObserver(() => {
      handleFilterUpdate();
    });
    
    gridObserver.observe(collectionGrid, {
      childList: true,
      subtree: true
    });
  }
};

// Run everything when the page first loads.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAnimations);
} else {
  initAnimations();
}

function initAnimations() {
  setupElementObserver();

  // Find and observe elements that are already on the page.
  const initialElements = document.querySelectorAll('.scroll-trigger');
  observeElements(initialElements);

  // Set up the observer to watch for new elements being added later.
  setupPageObserver();
  
  // Set up collection grid watcher
  watchCollectionGrid();
}

// Also run on page show (for back/forward cache)
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    handleFilterUpdate();
  }
});

// Emergency fallback: If products aren't visible after 2 seconds, force them visible
setTimeout(() => {
  const hiddenProducts = document.querySelectorAll('.product-grid__item.scroll-trigger:not(.is-visible)');
  if (hiddenProducts.length > 0) {
    console.warn('Found hidden products, forcing visibility');
    hiddenProducts.forEach(el => {
      el.classList.add('is-visible');
      animatedElements.add(el);
    });
  }
}, 2000);